import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import api from "./lib/api";

// Eager (always-mounted or first-paint critical)
import Login from "@/pages/Login";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import SuperAdminLayout from "@/layouts/SuperAdminLayout";
import TenantAdminLayout from "@/layouts/TenantAdminLayout";
import ParentLayout from "@/layouts/ParentLayout";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import PWAPrompts from "@/components/PWAPrompts";
import { Toaster } from "@/components/ui/sonner";

// Lazy-loaded pages (code-split into separate chunks, downloaded on demand)
const SuperAdminDashboard = lazy(() => import("@/pages/SuperAdmin/Dashboard"));
const TenantList = lazy(() => import("@/pages/SuperAdmin/TenantList"));
const CreateTenant = lazy(() => import("@/pages/SuperAdmin/CreateTenant"));
const AuditLogs = lazy(() => import("@/pages/SuperAdmin/AuditLogs"));
const TenantDashboard = lazy(() => import("@/pages/TenantAdmin/Dashboard"));
const Centers = lazy(() => import("@/pages/TenantAdmin/Centers"));
const CenterDetail = lazy(() => import("@/pages/Centers/CenterDetail"));
const ChildrenList = lazy(() => import("@/pages/Children/ChildrenList"));
const ChildProfile = lazy(() => import("@/pages/Children/ChildProfile"));
const AddChildPage = lazy(() => import("@/pages/Children/AddChildPage"));
const ActivityLog = lazy(() => import("@/pages/Activities/ActivityLog"));
const ActivityReports = lazy(() => import("@/pages/Activities/ActivityReports"));
const RoomsList = lazy(() => import("@/pages/Rooms/RoomsList"));
const ProgramsList = lazy(() => import("@/pages/Programs/ProgramsList"));
const ComplianceReportsList = lazy(() => import("@/pages/Compliance/ComplianceReportsList"));
const CreateComplianceReport = lazy(() => import("@/pages/Compliance/CreateComplianceReport"));
const ComplianceReportDetail = lazy(() => import("@/pages/Compliance/ComplianceReportDetail"));
const UploadWorkSamples = lazy(() => import("@/pages/WorkSamples/UploadWorkSamples"));
const ParentCompliance = lazy(() => import("@/pages/Parent/ParentCompliance"));
const ParentDashboard = lazy(() => import("@/pages/Parent/ParentDashboard"));
const ParentChildProfile = lazy(() => import("@/pages/Parent/ParentChildProfile"));
const ParentComplianceDetail = lazy(() => import("@/pages/Parent/ParentComplianceDetail"));
const ParentBilling = lazy(() => import("@/pages/Parent/ParentBilling"));
const PrivacyPolicyPage = lazy(() => import("@/pages/Legal/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/Legal/TermsOfServicePage"));
const CookiePolicyPage = lazy(() => import("@/pages/Legal/CookiePolicyPage"));
const ParentCheckin = lazy(() => import("@/pages/Parent/ParentCheckin"));
const Sessions = lazy(() => import("@/pages/Sessions"));
const EnrollmentDashboard = lazy(() => import("@/pages/Enrollment/EnrollmentDashboard"));
const LeadsList = lazy(() => import("@/pages/Enrollment/LeadsList"));
const LeadDetail = lazy(() => import("@/pages/Enrollment/LeadDetail"));
const AddLead = lazy(() => import("@/pages/Enrollment/AddLead"));
const RegistrationDocumentReview = lazy(() => import("@/pages/Enrollment/RegistrationDocumentReview"));
const PublicTourBooking = lazy(() => import("@/pages/Public/TourBooking"));
const PublicRegistration = lazy(() => import("@/pages/Public/Registration"));
const RegistrationV2 = lazy(() => import("@/pages/Public/RegistrationV2"));
const AftercareRegistration = lazy(() => import("@/pages/Public/AftercareRegistration"));
const ShortLinkRedirect = lazy(() => import("@/pages/Public/ShortLinkRedirect"));
const SetupAccountPage = lazy(() => import("@/pages/Auth/SetupAccountPage"));
const ResetPasswordPage = lazy(() => import("@/pages/Auth/ResetPasswordPage"));
const EmailTemplatesPage = lazy(() => import("@/pages/Settings/EmailTemplatesPage"));
const EmailSettingsPage = lazy(() => import("@/pages/Settings/EmailSettingsPage"));
const ParentInvitationsPage = lazy(() => import("@/pages/Settings/ParentInvitationsPage"));
const WelcomeEmailSettings = lazy(() => import("@/pages/Settings/WelcomeEmailSettings"));
const GeneralSettingsPage = lazy(() => import("@/pages/Settings/GeneralSettingsPage"));
const ParentsListPage = lazy(() => import("@/pages/Parents/ParentsListPage"));
const EmailComposerPage = lazy(() => import("@/pages/Email/EmailComposerPage"));
const SentEmailsPage = lazy(() => import("@/pages/Email/SentEmailsPage"));
const DraftsPage = lazy(() => import("@/pages/Email/DraftsPage"));
const ScheduledEmailsPage = lazy(() => import("@/pages/Email/ScheduledEmailsPage"));
const AttendanceV2Page = lazy(() => import("@/pages/Attendance/AttendanceV2Page"));
const PermissionsPage = lazy(() => import("@/pages/Settings/PermissionsPage"));
const PrivacySettingsPage = lazy(() => import("@/pages/Settings/PrivacySettingsPage"));
const KioskCheckinPage = lazy(() => import("@/pages/Kiosk/KioskCheckinPage"));
const BillingSettingsPage = lazy(() => import("@/pages/Billing/BillingSettingsPage"));
const InvoicesPage = lazy(() => import("@/pages/Billing/InvoicesPage"));
const InvoiceGeneratePage = lazy(() => import("@/pages/Billing/InvoiceGeneratePage"));
const PaymentsPage = lazy(() => import("@/pages/Billing/PaymentsPage"));
const DailyLogsPage = lazy(() => import("@/pages/DailyLogs/DailyLogsPage"));
const NapTrackerPage = lazy(() => import("@/pages/DailyLogs/NapTrackerPage"));
const MoodReportsPage = lazy(() => import("@/pages/DailyLogs/MoodReportsPage"));
const ActivityTypesPage = lazy(() => import("@/pages/Settings/ActivityTypesPage"));
const ParentActivityFeed = lazy(() => import("@/pages/Parent/ParentActivityFeed"));
const StaffDashboardPage = lazy(() => import("@/pages/Staff/StaffDashboardPage"));
const StaffDirectoryPage = lazy(() => import("@/pages/Staff/StaffDirectoryPage"));
const StaffProfilePage = lazy(() => import("@/pages/Staff/StaffProfilePage"));
const StaffAttendancePage = lazy(() => import("@/pages/Staff/StaffAttendancePage"));
const LeaveRequestsPage = lazy(() => import("@/pages/Staff/LeaveRequestsPage"));
const TrainingManagementPage = lazy(() => import("@/pages/Staff/TrainingManagementPage"));
const PerformanceSettingsPage = lazy(() => import("@/pages/Staff/PerformanceSettingsPage"));
const StaffReviewsPage = lazy(() => import("@/pages/Staff/StaffReviewsPage"));
const MyProfilePage = lazy(() => import("@/pages/Staff/MyProfilePage"));
// Curriculum (Phase 8)
const CurriculumSettingsPage = lazy(() => import("@/pages/Curriculum/CurriculumSettingsPage"));
const LessonPlansPage = lazy(() => import("@/pages/Curriculum/LessonPlansPage"));
const LessonPlanFormPage = lazy(() => import("@/pages/Curriculum/LessonPlanFormPage"));
const LessonPlanDetailPage = lazy(() => import("@/pages/Curriculum/LessonPlanDetailPage"));
const AILessonPlannerPage = lazy(() => import("@/pages/Curriculum/AILessonPlannerPage"));
const ObservationsPage = lazy(() => import("@/pages/Curriculum/ObservationsPage"));
const ObservationFormPage = lazy(() => import("@/pages/Curriculum/ObservationFormPage"));
const ObservationDetailPage = lazy(() => import("@/pages/Curriculum/ObservationDetailPage"));
const WorkSamplesPage = lazy(() => import("@/pages/Curriculum/WorkSamplesPage"));
const MilestonesPage = lazy(() => import("@/pages/Curriculum/MilestonesPage"));
// CRM (Phase 9)
const CRMDashboard = lazy(() => import("@/pages/CRM/CRMDashboard"));
const CRMLeadsListPage = lazy(() => import("@/pages/CRM/LeadsListPage"));
const CRMLeadDetailPage = lazy(() => import("@/pages/CRM/LeadDetailPage"));
const NewLeadPage = lazy(() => import("@/pages/CRM/NewLeadPage"));
const PipelinePage = lazy(() => import("@/pages/CRM/PipelinePage"));
const ToursPage = lazy(() => import("@/pages/CRM/ToursPage"));
const FollowupsPage = lazy(() => import("@/pages/CRM/FollowupsPage"));
const CRMPublicTourBooking = lazy(() => import("@/pages/CRM/PublicTourBooking"));
// Analytics (Phase 10)
const AnalyticsDashboard = lazy(() => import("@/pages/Analytics/AnalyticsDashboard"));
const EnrollmentAnalytics = lazy(() => import("@/pages/Analytics/EnrollmentAnalytics"));
const AttendanceAnalytics = lazy(() => import("@/pages/Analytics/AttendanceAnalytics"));
const FinancialAnalytics = lazy(() => import("@/pages/Analytics/FinancialAnalytics"));
const ComplianceAnalytics = lazy(() => import("@/pages/Analytics/ComplianceAnalytics"));
const OccupancyAnalytics = lazy(() => import("@/pages/Analytics/OccupancyAnalytics"));
const CRMAnalytics = lazy(() => import("@/pages/Analytics/CRMAnalytics"));
const ExportReports = lazy(() => import("@/pages/Analytics/ExportReports"));
const StaffPerformance = lazy(() => import("@/pages/Analytics/StaffPerformance"));
const ScheduledReports = lazy(() => import("@/pages/Analytics/ScheduledReports"));
const EngagementAnalytics = lazy(() => import("@/pages/Analytics/AnalyticsDashboardPage"));
// Billing extras
const OutstandingPaymentsPage = lazy(() => import("@/pages/Billing/OutstandingPaymentsPage"));
// Communication
const ParentMessagesPage = lazy(() => import("@/pages/Communication/ParentMessagesPage"));
const CommentFeedPage = lazy(() => import("@/pages/Communication/CommentFeedPage"));
const ActivityEngagementPage = lazy(() => import("@/pages/Communication/ActivityEngagementPage"));
// Parent portal
const ParentPortalMessagesPage = lazy(() => import("@/pages/Parent/ParentMessagesPage"));
const ParentPortalMilestonesPage = lazy(() => import("@/pages/Parent/ParentMilestonesPage"));
const ParentWorkSamplesPage = lazy(() => import("@/pages/Parent/ParentWorkSamplesPage"));
const ParentLessonPlansPage = lazy(() => import("@/pages/Parent/ParentLessonPlansPage"));
const ParentHomeworkPage = lazy(() => import("@/pages/Parent/ParentHomeworkPage"));
const ParentObservationsPage = lazy(() => import("@/pages/Parent/ParentObservationsPage"));
// Settings extras
const BulkImportPage = lazy(() => import("@/pages/Settings/BulkImportPage"));
const ChildrenCleanupPage = lazy(() => import("@/pages/Settings/ChildrenCleanup"));

// Lightweight fallback while a lazy chunk is downloading
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh] bg-[#F8FAF8]" data-testid="page-loader">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-4 border-sage-200 border-t-sage-500 animate-spin"></div>
        <div className="text-sage-600 text-sm font-medium">Loading…</div>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);

  // Verify token and refresh user data on app load
  const verifyAndRefreshSession = async () => {
    const token = localStorage.getItem("access_token");
    const storedUser = localStorage.getItem("user");
    
    if (!token || !storedUser) {
      setLoading(false);
      return;
    }

    try {
      // Verify token is still valid by calling /me endpoint
      const response = await api.get("/auth/me");
      
      const userData = response.data;
      setUser(userData);
      
      // Refresh tenant and centers data
      if (userData.role !== 'super_admin' && userData.tenant_id) {
        try {
          const [tenantRes, centersRes] = await Promise.all([
            api.get(`/tenants/${userData.tenant_id}`).catch(() => null),
            api.get("/tenant/centers").catch(() => ({ data: [] }))
          ]);
          
          if (tenantRes?.data) {
            setTenant(tenantRes.data);
            localStorage.setItem("tenant", JSON.stringify(tenantRes.data));
          }
          if (centersRes?.data) {
            setCenters(centersRes.data);
            localStorage.setItem("centers", JSON.stringify(centersRes.data));
          }
        } catch (e) {
          // Use cached data if refresh fails
          const storedTenant = localStorage.getItem("tenant");
          const storedCenters = localStorage.getItem("centers");
          if (storedTenant) setTenant(JSON.parse(storedTenant));
          if (storedCenters) setCenters(JSON.parse(storedCenters));
        }
      }
      
      localStorage.setItem("user", JSON.stringify(userData));
    } catch (error) {
      console.error("Session verification failed:", error);
      
      // Token might be expired, try to refresh it
      try {
        const refreshResponse = await api.post("/auth/refresh", {});
        
        const { access_token, user: refreshedUser } = refreshResponse.data;
        if (access_token) {
          localStorage.setItem("access_token", access_token);
        }
        
        if (refreshedUser) {
          setUser(refreshedUser);
          localStorage.setItem("user", JSON.stringify(refreshedUser));
        } else {
          // Parse stored user if refresh doesn't return user
          const storedUserData = localStorage.getItem("user");
          if (storedUserData) setUser(JSON.parse(storedUserData));
        }
        
        // Load cached tenant/centers
        const storedTenant = localStorage.getItem("tenant");
        const storedCenters = localStorage.getItem("centers");
        if (storedTenant) setTenant(JSON.parse(storedTenant));
        if (storedCenters) setCenters(JSON.parse(storedCenters));
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        // Clear everything and force re-login
        localStorage.clear();
        setUser(null);
        setTenant(null);
        setCenters([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyAndRefreshSession();
  }, []);

  const handleLogin = (userData, tenantData, centersData, accessToken) => {
    setUser(userData);
    setTenant(tenantData);
    setCenters(centersData || []);
    
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("access_token", accessToken);
    if (tenantData) localStorage.setItem("tenant", JSON.stringify(tenantData));
    if (centersData) localStorage.setItem("centers", JSON.stringify(centersData));
    
    // Track parent session for analytics
    if (userData?.role === "parent") {
      api.post("/analytics/parent/session", 
        { session_type: "login", platform: "web" }
      ).catch(() => {}); // Silent fail
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout", {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    setUser(null);
    setTenant(null);
    setCenters([]);
    localStorage.clear();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F8FAF8]">
        <div className="text-sage-500 text-lg font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            
            {/* Public Routes (no auth required) */}
            <Route path="/book-tour/:tenantSlug/:centerSlug" element={<PublicTourBooking />} />
            <Route path="/register/:tenantSlug/:centerSlug" element={<PublicRegistration />} />
            <Route path="/register-v2/:tenantSlug/:centerSlug" element={<RegistrationV2 />} />
            <Route path="/aftercare/:tenantSlug/:centerSlug" element={<AftercareRegistration />} />
            {/* Short Link Redirect */}
            <Route path="/r/:slug" element={<ShortLinkRedirect />} />
            {/* Phase 9: CRM Public Tour Booking */}
            <Route path="/:tenantSlug/:centerSlug/book-tour" element={<CRMPublicTourBooking />} />
            
            {/* Phase 5: Kiosk Check-in (public) */}
            <Route path="/kiosk/:tenantSlug/:centerSlug" element={<KioskCheckinPage />} />
            
            {/* Phase 4: Account setup routes (public, no auth) */}
            <Route path="/setup-account/:token" element={<SetupAccountPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            
            <Route
              path="/login"
              element={user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />}
            />
            
            {/* Force password change if required */}
            <Route
              path="/change-password"
              element={
                user?.must_change_password ? (
                  <ChangePasswordPage 
                    user={user} 
                    onPasswordChanged={() => setUser({ ...user, must_change_password: false })} 
                  />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            
            {/* Redirect to password change if required */}
            {user && user.must_change_password ? (
              <Route path="*" element={<Navigate to="/change-password" />} />
            ) : user && user.role === "super_admin" ? (
              <Route path="/" element={<SuperAdminLayout user={user} onLogout={handleLogout} />}>
                <Route index element={<SuperAdminDashboard />} />
                <Route path="tenants" element={<TenantList />} />
                <Route path="tenants/create" element={<CreateTenant />} />
                <Route path="audit-logs" element={<AuditLogs />} />
                <Route path="sessions" element={<Sessions />} />
              </Route>
            ) : user && user.role === "tenant_admin" ? (
              <Route path="/" element={<TenantAdminLayout user={user} tenant={tenant} centers={centers} onLogout={handleLogout} />}>
                <Route index element={<TenantDashboard />} />
                <Route path="children" element={<ChildrenList />} />
                <Route path="children/new" element={<AddChildPage />} />
                <Route path="children/:childId" element={<ChildProfile />} />
                <Route path="work-samples/upload" element={<UploadWorkSamples />} />
                <Route path="activities/log" element={<ActivityLog />} />
                <Route path="activities/reports" element={<ActivityReports />} />
                <Route path="rooms" element={<RoomsList />} />
                <Route path="programs" element={<ProgramsList />} />
                <Route path="compliance-reports" element={<ComplianceReportsList />} />
                <Route path="compliance-reports/create" element={<CreateComplianceReport />} />
                <Route path="compliance-reports/:reportId" element={<ComplianceReportDetail />} />
                <Route path="centers" element={<Centers />} />
                <Route path="centers/:centerId" element={<CenterDetail />} />
                <Route path="centers/:centerId/welcome-email" element={<WelcomeEmailSettings />} />
                <Route path="sessions" element={<Sessions />} />
                <Route path="parents" element={<ParentsListPage />} />
                {/* Enrollment Routes */}
                <Route path="enrollment" element={<EnrollmentDashboard />} />
                <Route path="enrollment/leads" element={<LeadsList />} />
                <Route path="enrollment/leads/new" element={<AddLead />} />
                <Route path="enrollment/leads/:leadId" element={<LeadDetail />} />
                <Route path="enrollment/registration/:formId/documents" element={<RegistrationDocumentReview />} />
                {/* Phase 4: Settings Routes */}
                <Route path="settings/email-templates" element={<EmailTemplatesPage />} />
                <Route path="settings/email" element={<EmailSettingsPage />} />
                <Route path="settings/parent-invitations" element={<ParentInvitationsPage />} />
                {/* Simple Email Composer Routes */}
                <Route path="email/compose" element={<EmailComposerPage />} />
                <Route path="email/sent" element={<SentEmailsPage />} />
                <Route path="email/scheduled" element={<ScheduledEmailsPage />} />
                <Route path="email/drafts" element={<DraftsPage />} />
                {/* Attendance */}
                <Route path="attendance" element={<AttendanceV2Page />} />
                {/* My Profile - available to admins too */}
                <Route path="my-profile" element={<MyProfilePage />} />
                {/* Phase 5: Permissions */}
                <Route path="settings/permissions" element={<PermissionsPage />} />
                <Route path="settings/privacy" element={<PrivacySettingsPage />} />
                <Route path="settings/general" element={<GeneralSettingsPage />} />
                <Route path="settings/bulk-import" element={<BulkImportPage />} />
                <Route path="settings/children-cleanup" element={<ChildrenCleanupPage />} />
                {/* Phase 5: Billing */}
                <Route path="billing/settings" element={<BillingSettingsPage />} />
                <Route path="billing/invoices" element={<InvoicesPage />} />
                <Route path="billing/invoices/generate" element={<InvoiceGeneratePage />} />
                <Route path="billing/payments" element={<PaymentsPage />} />
                <Route path="billing/outstanding" element={<OutstandingPaymentsPage />} />
                {/* Phase 6: Daily Logs */}
                <Route path="daily-logs" element={<DailyLogsPage />} />
                <Route path="daily-logs/naps" element={<NapTrackerPage />} />
                <Route path="daily-logs/mood-reports" element={<MoodReportsPage />} />
                <Route path="settings/activity-types" element={<ActivityTypesPage />} />
                {/* Phase 7: Staff Management + HR */}
                <Route path="staff-management" element={<StaffDashboardPage />} />
                <Route path="staff" element={<StaffDirectoryPage />} />
                <Route path="staff/attendance" element={<StaffAttendancePage />} />
                <Route path="staff/new" element={<StaffProfilePage />} />
                <Route path="staff/:staffId" element={<StaffProfilePage />} />
                <Route path="staff/leave-requests" element={<LeaveRequestsPage />} />
                <Route path="staff/training" element={<TrainingManagementPage />} />
                <Route path="staff/performance-settings" element={<PerformanceSettingsPage />} />
                <Route path="staff/reviews" element={<StaffReviewsPage />} />
                {/* Phase 8: Curriculum */}
                <Route path="curriculum/settings" element={<CurriculumSettingsPage />} />
                <Route path="curriculum/lessons" element={<LessonPlansPage />} />
                <Route path="curriculum/lessons/new" element={<LessonPlanFormPage />} />
                <Route path="curriculum/lessons/:id" element={<LessonPlanDetailPage />} />
                <Route path="curriculum/lessons/:id/edit" element={<LessonPlanFormPage />} />
                <Route path="curriculum/ai-planner" element={<AILessonPlannerPage />} />
                <Route path="curriculum/observations" element={<ObservationsPage />} />
                <Route path="curriculum/observations/new" element={<ObservationFormPage />} />
                <Route path="curriculum/observations/:id" element={<ObservationDetailPage />} />
                <Route path="curriculum/observations/:id/edit" element={<ObservationFormPage />} />
                <Route path="curriculum/work-samples" element={<WorkSamplesPage />} />
                <Route path="curriculum/milestones" element={<MilestonesPage />} />
                {/* Communication */}
                <Route path="messages" element={<ParentMessagesPage />} />
                <Route path="comments" element={<CommentFeedPage />} />
                <Route path="activity-engagement" element={<ActivityEngagementPage />} />
                {/* Phase 9: CRM Routes */}
                <Route path="crm" element={<CRMDashboard />} />
                <Route path="crm/leads" element={<CRMLeadsListPage />} />
                <Route path="crm/leads/new" element={<NewLeadPage />} />
                <Route path="crm/leads/:leadId" element={<CRMLeadDetailPage />} />
                <Route path="crm/pipeline" element={<PipelinePage />} />
                <Route path="crm/tours" element={<ToursPage />} />
                <Route path="crm/followups" element={<FollowupsPage />} />
                {/* Phase 10: Analytics Routes */}
                <Route path="analytics" element={<AnalyticsDashboard />} />
                <Route path="analytics/enrollment" element={<EnrollmentAnalytics />} />
                <Route path="analytics/attendance" element={<AttendanceAnalytics />} />
                <Route path="analytics/financial" element={<FinancialAnalytics />} />
                <Route path="analytics/compliance" element={<ComplianceAnalytics />} />
                <Route path="analytics/occupancy" element={<OccupancyAnalytics />} />
                <Route path="analytics/crm" element={<CRMAnalytics />} />
                <Route path="analytics/staff" element={<StaffPerformance />} />
                <Route path="analytics/export" element={<ExportReports />} />
                <Route path="analytics/scheduled" element={<ScheduledReports />} />
                <Route path="analytics/engagement" element={<EngagementAnalytics />} />
              </Route>
            ) : user && user.role === "staff" ? (
              <Route path="/" element={<TenantAdminLayout user={user} tenant={tenant} centers={centers} onLogout={handleLogout} />}>
                <Route index element={<TenantDashboard />} />
                <Route path="children" element={<ChildrenList />} />
                <Route path="children/new" element={<AddChildPage />} />
                <Route path="children/:childId" element={<ChildProfile />} />
                <Route path="work-samples/upload" element={<UploadWorkSamples />} />
                <Route path="activities/log" element={<ActivityLog />} />
                <Route path="activities/reports" element={<ActivityReports />} />
                <Route path="rooms" element={<RoomsList />} />
                <Route path="programs" element={<ProgramsList />} />
                <Route path="compliance-reports" element={<ComplianceReportsList />} />
                <Route path="compliance-reports/create" element={<CreateComplianceReport />} />
                <Route path="compliance-reports/:reportId" element={<ComplianceReportDetail />} />
                <Route path="sessions" element={<Sessions />} />
                {/* Enrollment Routes */}
                <Route path="enrollment" element={<EnrollmentDashboard />} />
                <Route path="enrollment/leads" element={<LeadsList />} />
                <Route path="enrollment/leads/new" element={<AddLead />} />
                <Route path="enrollment/leads/:leadId" element={<LeadDetail />} />
                <Route path="centers" element={<Centers />} />
                <Route path="centers/:centerId" element={<CenterDetail />} />
                {/* Simple Email Composer Routes for Staff */}
                <Route path="email/compose" element={<EmailComposerPage />} />
                <Route path="email/sent" element={<SentEmailsPage />} />
                <Route path="email/scheduled" element={<ScheduledEmailsPage />} />
                <Route path="email/drafts" element={<DraftsPage />} />
                {/* Attendance */}
                <Route path="attendance" element={<AttendanceV2Page />} />
                <Route path="staff/attendance" element={<StaffAttendancePage />} />
                {/* My Profile - Staff Self-Service */}
                <Route path="my-profile" element={<MyProfilePage />} />
                {/* Phase 6: Daily Logs for Staff */}
                <Route path="daily-logs" element={<DailyLogsPage />} />
                <Route path="daily-logs/naps" element={<NapTrackerPage />} />
                <Route path="daily-logs/mood-reports" element={<MoodReportsPage />} />
                {/* Phase 8: Curriculum for Staff */}
                <Route path="curriculum/settings" element={<CurriculumSettingsPage />} />
                <Route path="curriculum/lessons" element={<LessonPlansPage />} />
                <Route path="curriculum/lessons/new" element={<LessonPlanFormPage />} />
                <Route path="curriculum/lessons/:id" element={<LessonPlanDetailPage />} />
                <Route path="curriculum/lessons/:id/edit" element={<LessonPlanFormPage />} />
                <Route path="curriculum/ai-planner" element={<AILessonPlannerPage />} />
                <Route path="curriculum/observations" element={<ObservationsPage />} />
                <Route path="curriculum/observations/new" element={<ObservationFormPage />} />
                <Route path="curriculum/observations/:id" element={<ObservationDetailPage />} />
                <Route path="curriculum/observations/:id/edit" element={<ObservationFormPage />} />
                <Route path="curriculum/work-samples" element={<WorkSamplesPage />} />
                <Route path="curriculum/milestones" element={<MilestonesPage />} />
                {/* Communication Routes for Staff */}
                <Route path="messages" element={<ParentMessagesPage />} />
                <Route path="comments" element={<CommentFeedPage />} />
                <Route path="activity-engagement" element={<ActivityEngagementPage />} />
                {/* Phase 9: CRM Routes for Staff */}
                <Route path="crm" element={<CRMDashboard />} />
                <Route path="crm/leads" element={<CRMLeadsListPage />} />
                <Route path="crm/leads/new" element={<NewLeadPage />} />
                <Route path="crm/leads/:leadId" element={<CRMLeadDetailPage />} />
                <Route path="crm/pipeline" element={<PipelinePage />} />
                <Route path="crm/tours" element={<ToursPage />} />
                <Route path="crm/followups" element={<FollowupsPage />} />
                {/* Phase 10: Analytics Routes for Staff */}
                <Route path="analytics" element={<AnalyticsDashboard />} />
                <Route path="analytics/enrollment" element={<EnrollmentAnalytics />} />
                <Route path="analytics/attendance" element={<AttendanceAnalytics />} />
                <Route path="analytics/financial" element={<FinancialAnalytics />} />
                <Route path="analytics/compliance" element={<ComplianceAnalytics />} />
                <Route path="analytics/occupancy" element={<OccupancyAnalytics />} />
                <Route path="analytics/crm" element={<CRMAnalytics />} />
                <Route path="analytics/staff" element={<StaffPerformance />} />
                <Route path="analytics/export" element={<ExportReports />} />
                <Route path="analytics/scheduled" element={<ScheduledReports />} />
              </Route>
            ) : user && user.role === "parent" ? (
              <Route path="/" element={<ParentLayout user={user} onLogout={handleLogout} />}>
                <Route index element={<ParentDashboard />} />
                <Route path="children/:childId" element={<ParentChildProfile />} />
                <Route path="reports" element={<ParentCompliance />} />
                <Route path="reports/:reportId" element={<ParentComplianceDetail />} />
                <Route path="billing" element={<ParentBilling />} />
                <Route path="checkin" element={<ParentCheckin />} />
                {/* Phase 6: Activity Feed */}
                <Route path="activity" element={<ParentActivityFeed />} />
                <Route path="activity/:childId" element={<ParentActivityFeed />} />
                {/* Parent Messages & Milestones */}
                <Route path="messages" element={<ParentPortalMessagesPage />} />
                <Route path="milestones" element={<ParentPortalMilestonesPage />} />
                {/* Parent Learning - Work Samples, Lesson Plans, Homework, Observations */}
                <Route path="work-samples" element={<ParentWorkSamplesPage />} />
                <Route path="lesson-plans" element={<ParentLessonPlansPage />} />
                <Route path="homework" element={<ParentHomeworkPage />} />
                <Route path="observations" element={<ParentObservationsPage />} />
                {/* Privacy & GDPR */}
                <Route path="settings/privacy" element={<PrivacySettingsPage />} />
              </Route>
            ) : (
              <Route path="*" element={<Navigate to="/login" />} />
            )}
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" />
      <CookieConsentBanner />
      <PWAPrompts />
    </div>
  );
}

export default App;
